import ModalBackButton from "../components/ModalBackButton";
import PaymentMethodButton from "../components/PaymentMethodButton";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import EVMWalletsLogo from "../logos/EVMWalletsLogo";
import PaymentMethodSmallButton from "../components/PaymentMethodSmallButton";
import { InteracLogo } from "../logos/InteracLogo";
import { SectionTitle } from "../components/SectionTitle";

export type WithdrawPaymentMethod = "wallet" | "interac";

type Props = {
  selectedPaymentMethod?: WithdrawPaymentMethod;
  onSelect: (paymentMethod: WithdrawPaymentMethod) => void;
  onBack?: () => void;
};

export function WithdrawPaymentMethod({
  selectedPaymentMethod,
  onBack,
  onSelect,
}: Props) {
  const depositConfig = useGlideDepositConfig();

  const fiatWithdrawMethods = depositConfig.popupsBlocked
    ? []
    : depositConfig.paymentMethods
        .filter((method) => method === "interac")
        .filter(
          (method) => !depositConfig.excludeFundingSources?.includes(method),
        );

  const totalFiatWithdrawOptions = fiatWithdrawMethods.length;
  const selectedCurrency = depositConfig.currency;

  return (
    <>
      {onBack ? <ModalBackButton onClick={() => onBack()} /> : null}

      <TransactionCheckoutHeader
        title={`Withdraw${selectedCurrency ? ` ${selectedCurrency.symbol}` : ""}`}
      />

      <div className="space-y-3">
        <PaymentMethodButton
          icon={<EVMWalletsLogo className="h-8 w-8" />}
          title="Wallet"
          subtitle={"Send tokens to your wallet on any chain"}
          onClick={() => onSelect("wallet")}
          loading={selectedPaymentMethod === "wallet"}
        />

        {/* Show fiat withdrawal options if available */}
        {totalFiatWithdrawOptions > 0 ? (
          <>
            <div className="relative flex items-center">
              <div className="flex-1 border-t border-[--color-text-secondary]"></div>
              <span className="px-2 text-[--color-text-secondary] text-xs uppercase">
                Or
              </span>
              <div className="flex-1 border-t border-[--color-text-secondary]"></div>
            </div>

            {totalFiatWithdrawOptions === 1 ? (
              <>
                {fiatWithdrawMethods[0] === "interac" ? (
                  <PaymentMethodButton
                    icon={<InteracLogo className="h-6 w-6" />}
                    title="Interac"
                    subtitle={`Withdraw instantly via Interac e-Transfer`}
                    loading={selectedPaymentMethod === "interac"}
                    onClick={() => onSelect("interac")}
                    disabled={depositConfig.chain?.isTestnet}
                  />
                ) : null}
              </>
            ) : (
              <>
                <SectionTitle
                  title={`Withdraw${selectedCurrency ? ` ${selectedCurrency.symbol}` : " fiat"}`}
                  subtitle="Pick a provider to withdraw funds to your bank account"
                />

                <div
                  className="grid gap-3 mt-3"
                  style={{
                    gridTemplateColumns: `repeat(${totalFiatWithdrawOptions}, minmax(0, 1fr))`,
                  }}
                >
                  {fiatWithdrawMethods.map((method) => {
                    switch (method) {
                      case "interac":
                        return (
                          <PaymentMethodSmallButton
                            key={method}
                            icon={<InteracLogo className="h-6 w-6" />}
                            title="Interac"
                            loading={selectedPaymentMethod === "interac"}
                            onClick={() => onSelect("interac")}
                            disabled={depositConfig.chain?.isTestnet}
                          />
                        );
                      default:
                        return null;
                    }
                  })}
                </div>
              </>
            )}
          </>
        ) : null}
      </div>
    </>
  );
}
